import * as React from "react";
import { ColorPicker, Popover } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  useContentSlideStore,
  type ImpliedColorRow,
} from "@/state/contentSlideStore";
import { fetchFormatTemplate } from "@/utils/sharePricePerformance/fetchFormatTemplates";

export type { ImpliedColorRow };

const TOOLTIP_TEXT = "Colors shown are based on the selected format template";

/** Example series labels until benchmark selections are wired from step 2. */
export const DEFAULT_IMPLIED_COLOR_ROWS: ImpliedColorRow[] = [
  { label: "DropBox", rgb: "rgb(0,61,121)" },
  { label: "Box", rgb: "rgb(0,166,230)" },
  { label: 'Custom Index ("Primary Peers")', rgb: "rgb(64,192,160)" },
  { label: "Dow Jones Software", rgb: "rgb(210,190,120)" },
];

function shortenCompanyDisplayName(name: string): string {
  return name
    .replace(/\s+Inc\.?$/i, "")
    .replace(/\s+Corporation$/i, "")
    .replace(/\s+Corp\.?$/i, "")
    .trim();
}

function toRgbString(color: string | undefined): string {
  if (!color) return "rgb(0,0,0)";
  const trimmed = color.trim();
  if (trimmed.startsWith("rgb")) return trimmed;
  const hex = trimmed.replace(/^#/, "");
  if (hex.length !== 6) return trimmed;
  const n = parseInt(hex, 16);
  if (Number.isNaN(n)) return trimmed;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgb(${r},${g},${b})`;
}

function parseChartColors(formatData: string | undefined): string[] {
  if (!formatData) return [];
  try {
    const parsed = JSON.parse(formatData) as {
      colorsSection?: { chartColors?: Array<{ fillColor?: string }> };
    };
    return (parsed.colorsSection?.chartColors ?? [])
      .map((c) => toRgbString(c.fillColor))
      .filter(Boolean);
  } catch {
    return [];
  }
}

function mergeRowsWithChartColors(rows: ImpliedColorRow[], chartRgbs: string[]): ImpliedColorRow[] {
  const base = rows.length ? rows : DEFAULT_IMPLIED_COLOR_ROWS;
  return base.map((row, index) => ({
    ...row,
    rgb: chartRgbs[index] ?? row.rgb,
  }));
}

function applyPrimaryCompanyLabel(rows: ImpliedColorRow[], companyName: string | undefined): ImpliedColorRow[] {
  if (!companyName?.trim()) return rows;
  const next = [...rows];
  if (next[0]) {
    next[0] = { ...next[0], label: shortenCompanyDisplayName(companyName) };
  }
  return next;
}

type ImpliedColorsPanelProps = {
  rows?: ImpliedColorRow[];
};

export default function ImpliedColorsPanel({ rows: rowsProp }: ImpliedColorsPanelProps) {
  const selectedFormatTemplateId = useContentSlideStore((s) => s.selectedFormatTemplateId);
  const primaryCompany = useContentSlideStore((s) => s.primaryCompany);
  const impliedColorRows = useContentSlideStore((s) => s.impliedColorRows);
  const setImpliedColorRows = useContentSlideStore((s) => s.setImpliedColorRows);
  const updateImpliedColorRgb = useContentSlideStore((s) => s.updateImpliedColorRgb);

  const [loading, setLoading] = React.useState(false);
  const lastTemplateIdRef = React.useRef<string | null>(null);

  const rows = rowsProp ?? (impliedColorRows.length ? impliedColorRows : DEFAULT_IMPLIED_COLOR_ROWS);

  React.useEffect(() => {
    if (rowsProp) return;
    if (impliedColorRows.length > 0) return;
    setImpliedColorRows(applyPrimaryCompanyLabel(DEFAULT_IMPLIED_COLOR_ROWS, primaryCompany?.companyName));
  }, [rowsProp, impliedColorRows.length, primaryCompany?.companyName, setImpliedColorRows]);

  React.useEffect(() => {
    if (rowsProp) return;
    const prev = useContentSlideStore.getState().impliedColorRows;
    const base = prev.length ? prev : DEFAULT_IMPLIED_COLOR_ROWS;
    setImpliedColorRows(applyPrimaryCompanyLabel(base, primaryCompany?.companyName));
  }, [primaryCompany?.companyName, rowsProp, setImpliedColorRows]);

  React.useEffect(() => {
    if (rowsProp || !selectedFormatTemplateId) return undefined;

    const templateChanged = lastTemplateIdRef.current !== selectedFormatTemplateId;
    lastTemplateIdRef.current = selectedFormatTemplateId;
    if (!templateChanged) return undefined;

    let cancelled = false;
    setLoading(true);

    void fetchFormatTemplate(selectedFormatTemplateId)
      .then((format) => {
        if (cancelled) return;
        const chartRgbs = parseChartColors(format?.formatData);
        const prev = useContentSlideStore.getState().impliedColorRows;
        const withLabels = applyPrimaryCompanyLabel(
          prev.length ? prev : DEFAULT_IMPLIED_COLOR_ROWS,
          primaryCompany?.companyName,
        );
        setImpliedColorRows(mergeRowsWithChartColors(withLabels, chartRgbs));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFormatTemplateId, rowsProp, primaryCompany?.companyName, setImpliedColorRows]);

  const handleColorChange = React.useCallback(
    (index: number, rgb: string) => {
      if (rowsProp) return;
      updateImpliedColorRgb(index, rgb);
    },
    [rowsProp, updateImpliedColorRgb],
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-4 flex items-center gap-1.5">
        <h3 className="text-base font-semibold text-gray-900">Implied Colors</h3>
        <Popover content={TOOLTIP_TEXT} trigger="hover" overlayStyle={{ maxWidth: 280 }}>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded p-0.5 text-gray-500 transition-colors hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            aria-label="About implied colors"
          >
            <InfoCircleOutlined />
          </button>
        </Popover>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto  gap-3">
        {loading && rows.length === 0 ? (
          <p className="text-sm text-gray-500">Loading colors…</p>
        ) : (
          rows.map((row, index) => (
            <div key={`${row.label}-${index}`} className="flex flex-col items-start justify-between gap-1">
              <span className="min-w-0 flex-1 truncate text-sm font-bold text-gray-800" title={row.label}>
                {row.label}
              </span>
              <div className="pl-4">
                <ColorPicker
                  format="rgb"
                  showText
                  value={row.rgb}
                  disabled={Boolean(rowsProp)}
                  onChange={(color) => handleColorChange(index, color.toRgbString())}
                  styles={{
                    popupOverlayInner: { width: 280 },
                  }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
